import { useState } from 'react'
import type { QuestionResponse } from '@assessments/shared'
import { recordResponse } from '../response-autosave/api.js'
import { pendingAnswerWrites } from './questionMapping.js'

export interface DoneEditingProps {
  organizationSurveyId: string
  /** Reads the answers at call time — a snapshot taken at render would be stale by the
   * time `flush()` resolves, and would write its older value over a newer edit. */
  getResponses: () => Record<string, QuestionResponse>
  /** Question ids edited this session (a cleared answer must still be sent). */
  dirtyIds: ReadonlySet<string>
  /** True when autosave already stored the question's latest value unchanged. */
  isClean: (questionId: string) => boolean
  /** Resolves once no autosave is in flight. */
  flush: () => Promise<void>
  release: () => Promise<void>
  onExit: () => void
  /** Reports the save/release flow's progress so the caller can hold the answer inputs
   * closed for its whole duration. */
  onSavingChange?: (saving: boolean) => void
  disabled?: boolean
}

/**
 * The always-available "Done editing" exit: persists in-progress answers
 * with NO completion validation, then releases the lock, then navigates — persist must
 * run while the caller still holds the lock, because the write path is lock-guarded.
 * A failed save or a failed release both keep the editor on the section (button stays
 * disabled until each step settles) so no work is silently lost and the lock is never
 * abandoned client-side while still live on the server. These writes bypass autosave's
 * per-question coalescing, so they wait out anything autosave still has in flight —
 * otherwise that older save could land last and restore the value it was carrying.
 */
export function DoneEditing({
  organizationSurveyId,
  getResponses,
  dirtyIds,
  isClean,
  flush,
  release,
  onExit,
  onSavingChange,
  disabled = false,
}: DoneEditingProps) {
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [closeError, setCloseError] = useState(false)

  function reportSaving(next: boolean) {
    setSaving(next)
    onSavingChange?.(next)
  }

  async function handleDone() {
    setSaveError(false)
    setCloseError(false)
    reportSaving(true)
    try {
      await flush()
      const writes = pendingAnswerWrites(getResponses(), dirtyIds, isClean)
      await Promise.all(
        writes.map(([id, request]) => recordResponse(organizationSurveyId, id, request)),
      )
    } catch {
      setSaveError(true)
      reportSaving(false)
      return
    }
    try {
      await release()
    } catch {
      // Answers are saved; the lock release failed and is still held server-side.
      // Stay on the section (never navigate on a failed release) so the user can retry.
      setCloseError(true)
      reportSaving(false)
      return
    }
    reportSaving(false)
    onExit()
  }

  return (
    <>
      <button
        type="button"
        onClick={handleDone}
        disabled={disabled || saving}
        className="rounded-button border border-border bg-surface-page px-4 py-2 text-sm font-medium text-body hover:bg-surface-hover disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Done editing'}
      </button>
      {saveError && (
        <div role="alert" className="mt-3 text-sm text-danger">
          Couldn’t save your answers. Please try again.
        </div>
      )}
      {closeError && (
        <div role="alert" className="mt-3 text-sm text-danger">
          Your answers were saved, but we couldn’t close this section. Please try again.
        </div>
      )}
    </>
  )
}
